import { API, APINonPrefix, getDataCookie } from 'common';
import { subscribeSharedTopic } from 'socket/shared-stomp';
import {
  AppointmentHistoryPage, CallerIdService, CreateCustomerRequest, IncomingCall,
  PendingCall, QuickBookingItem, RecentAppointmentsResult,
} from './types';

// Caller ID v2 integration (docs/caller-id-v2-integration.md):
// ring events arrive via the STOMP topic and/or the native-shell bridge; all
// list/history data comes from REST. The pending list is the source of truth.


type Listener = (call: IncomingCall) => void;

const listeners = new Set<Listener>();
let bridgeListener: ((e: MessageEvent) => void) | null = null;
let topicUnsubscribe: (() => void) | null = null;
// invalidates an in-flight station-config fetch when the last subscriber
// leaves or a newer connect supersedes it (prevents duplicate subscriptions)
let generation = 0;

const shopId = () => getDataCookie(process.env.REACT_APP_API_X_KEY!) ?? '';

interface RingPayload {
  callerId?: string;
  customerId?: string | number | null;
  customerName?: string;
  phone?: string;
}

const emitRing = (payload: RingPayload) => {
  if (!payload?.callerId || !payload?.phone) return;
  const customerId = Number(payload.customerId);
  const call: IncomingCall = {
    id: payload.callerId,
    phone: payload.phone,
    receivedAt: Date.now(),
    customer: payload.customerId && !Number.isNaN(customerId)
      ? { id: customerId, name: payload.customerName || '' }
      : null,
  };
  listeners.forEach(cb => cb(call));
};

// stationNumber (= deviceCode) enables per-device topic routing; absent →
// shop-wide topic. Auth via the standard x-api-key (already on the API instance).
const fetchStationNumber = async (): Promise<string | null> => {
  try {
    const res = await API.get('admin/store/config-caller-id');
    const station = res.data?.payload?.stationNumber;
    return station ? String(station) : null;
  } catch {
    return null;
  }
};

const connectTransports = () => {
  const gen = ++generation;
  if (!shopId()) return; // no api key cookie — nothing to subscribe to

  // native bridge (React Native webview): only CALLER_BOOKING_REQUEST opens Caller ID
  bridgeListener = (e: MessageEvent) => {
    try {
      const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
      if (data?.type === 'CALLER_BOOKING_REQUEST' && data.caller) emitRing(data.caller);
    } catch {
      // non-JSON messages from other senders — ignore
    }
  };
  window.addEventListener('message', bridgeListener);

  fetchStationNumber().then(station => {
    // subscribers may have left (or a newer connect started) during the fetch
    if (gen !== generation || listeners.size === 0) return;
    const topic = station
      ? `/topic/inbound-call-booking/${shopId()}/${station}`
      : `/topic/inbound-call-booking/${shopId()}`;
    // shared connection (one socket app-wide); it reconnects on drops itself
    topicUnsubscribe = subscribeSharedTopic(topic, msg => {
      try {
        emitRing(JSON.parse(msg.body));
      } catch {
        // malformed message body — ignore
      }
    });
  });
};

const disconnectTransports = () => {
  generation++;
  if (bridgeListener) {
    window.removeEventListener('message', bridgeListener);
    bridgeListener = null;
  }
  topicUnsubscribe?.();
  topicUnsubscribe = null;
};

export const realCallerIdService: CallerIdService = {
  subscribeIncomingCalls(cb: Listener) {
    listeners.add(cb);
    if (listeners.size === 1) connectTransports();
    return () => {
      listeners.delete(cb);
      if (listeners.size === 0) disconnectTransports();
    };
  },

  subscribeBookingCompleted(cb: () => void) {
    if (!shopId()) return () => { };
    // shop-wide topic on the same shared connection; the backend publishes it
    // whenever an appointment is created/changed (docs/caller-id-v2-integration.md §2.1)
    return subscribeSharedTopic(`/topic/book-appointment-completed/${shopId()}`, () => cb());
  },

  async fetchPendingCalls(): Promise<PendingCall[]> {
    const res = await API.get('appointment/pending-caller-id');
    const rows: any[] = res.data?.payload ?? [];
    return rows.map(r => ({
      id: r.id,
      date: r.date,
      time: r.time,
      customerName: r.customerName || '',
      customerPhone: r.customerPhone || '',
      customerType: r.customerType === 'Regular' ? 'Regular' : 'New',
      customerId: r.customerId ?? null,
    }));
  },

  async sendSms(callerId: string, phone: string): Promise<void> {
    await API.post('appointment/send-sms-booking-online', { id: callerId, phone });
  },

  async createBooking(item: QuickBookingItem): Promise<void> {
    // v2 endpoint lives outside the API instance's /api/v1/ base
    await APINonPrefix.post('v2/appointment/quickBooking', [item]);
  },

  async createCustomer(req: CreateCustomerRequest): Promise<number> {
    // same action-wrapper convention as bookingAppointment/customer-update
    const res = await API.post('customer/save', {
      type: 'FEEDBACK/DASHBOARD/ON_CREATE_CUSTOMER',
      payload: {
        firstName: req.firstName,
        lastName: req.lastName,
        phone: req.phone,
        email: req.email,
        ...(req.birthday ? { birthday: req.birthday } : {}),
        ...(req.gender ? { gender: req.gender } : {}),
      },
    });
    const p = res.data?.payload;
    const id = typeof p === 'number' || typeof p === 'string' ? p : p?.id ?? p?.customerId;
    if (id === null || id === undefined || Number.isNaN(Number(id))) {
      throw new Error('customer/save returned no customer id');
    }
    return Number(id);
  },

  async getQuickServices() {
    const res = await API.get('service/quickBooking');
    return res.data?.payload ?? [];
  },

  async getCustomerRecentAppointments(customerId: number): Promise<RecentAppointmentsResult> {
    const res = await API.get('appointment/customer-recent-appointments', { params: { customerId } });
    return res.data.payload;
  },

  async getCustomerAppointments(customerId: number, page: number, pageSize: number): Promise<AppointmentHistoryPage> {
    const res = await API.get('appointment/customer-appointments', {
      // server does not validate paging — client must send sane values (doc §5)
      params: { customerId, page: Math.max(1, page), pageSize: Math.min(Math.max(1, pageSize), 50) },
    });
    return res.data.payload;
  },
};
